<template>
  <v-container class="contact-page" fluid>
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Agent Privileges <span class="thai">สิทธิพิเศษ PrincAgent</span></h1>
        <p class="subhead mb-0">Manage campaigns and privileges linked to insurance partners.</p>
      </div>
      <v-btn v-if="canAdd" class="setup-btn" color="primary" @click="$router.push({name: 'agent-privilege-create'})">
        <v-icon left small>mdi-plus</v-icon>
        Create Privilege
      </v-btn>
    </div>

    <v-card class="table-card">
      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field
            v-model="searchText"
            append-icon="mdi-magnify"
            outlined
            dense
            hide-details
            label="Search by campaign title..."
            clearable
            @input="onSearch"
          />
        </div>
        <v-select v-if="insurancePartnerOptionsWithAll.length > 1"
          v-model="selectedInsurancePartnerId"
          :items="insurancePartnerOptionsWithAll"
          outlined
          dense
          hide-details
          item-text="text"
          item-value="value"
          class="small-select"
          placeholder="All Insurance Partners"
          @input="onInsurancePartnerFilterChange"
        />
        <v-select v-if="isSuperAdmin"
          v-model="selectedOrg"
          :items="orgOptionsWithAll"
          outlined
          dense
          hide-details
          item-text="text"
          item-value="value"
          class="small-select"
          :disabled="isBranchLocked"
          placeholder="All Branches"
          @input="onOrgFilterChange"
        />
        <v-btn outlined class="filter-btn" :class="{'has-filtered': hasAppliedFilters}" @click="handleFilterButtonClick">
          <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
          {{ hasAppliedFilters ? 'Clear' : 'Filter' }}
        </v-btn>
      </div>

      <v-data-table
        :loading="isPageLoading"
        :headers="visibleHeaders"
        :items="list"
        :items-per-page="10"
        hide-default-footer
        class="contact-table"
      >
        <template v-slot:item.no="{index, item}">{{ rowNo(index, item) }}</template>

        <template v-slot:item.insurance_partner="{item}">
          <strong>{{ resolveInsurancePartnerName(item) }}</strong>
        </template>

        <template v-slot:item.campaign="{item}">
          <div class="campaign-wrap">
            <strong>{{ item.title_en || item.title_th || '' }}</strong>
            <div class="campaign-sub">{{ item.description_en || item.description_th || '' }}</div>
          </div>
        </template>

        <template v-slot:item.branch="{item}">
          <span class="branch-pill">{{ item.org_codename || '-' }}</span>
        </template>

        <template v-slot:item.valid_from="{item}">
          {{ formatDate(item.valid_from) }}
        </template>

        <template v-slot:item.valid_to="{item}">
          {{ formatDate(item.valid_to) }}
        </template>

        <template v-slot:item.action="{item}">
          <button v-if="canEdit" class="icon-btn" @click.stop="goToEdit(item)"><v-icon size="16">mdi-pencil-outline</v-icon></button>
          <button v-if="canDelete" class="icon-btn ml-2" @click.stop="remove(item)"><v-icon size="16">mdi-delete-outline</v-icon></button>
        </template>
      </v-data-table>

      <div class="table-foot">
        Showing 1 to {{ list.length }} of {{ list.length }} entries
      </div>
    </v-card>

  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'

export default {
  data() {
    return {
      searchText: '',
      selectedOrg: '',
      selectedInsurancePartnerId: '',
      localLoading: false,
      headers: [
        {text: 'NO.', value: 'no', sortable: false, width: 70},
        {text: 'INSURANCE PARTNER', value: 'insurance_partner', sortable: false},
        {text: 'CAMPAIGN TITLE', value: 'campaign', sortable: false},
        {text: 'BRANCH', value: 'branch', sortable: false, width: 170},
        {text: 'VALID FROM', value: 'valid_from', sortable: false, width: 180},
        {text: 'VALID TO', value: 'valid_to', sortable: false, width: 180},
        {text: 'ACTIONS', value: 'action', sortable: false, width: 120, align: 'center'}
      ]
    }
  },
  computed: {
    ...mapState('agentPrivileges', ['list', 'isLoading', 'orgOptions', 'insurancePartnerOptions']),
    ...mapState('auth', ['user']),
    canAdd() { return hasPermision('CanAddAgentPrivilege') },
    canEdit() { return hasPermision('CanEditAgentPrivilege') },
    canDelete() { return hasPermision('CanDeleteAgentPrivilege') },
    isBranchLocked() {
      return !this.isSuperAdmin || this.orgOptions.length === 1
    },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    orgOptionsWithAll() {
      return [{text: 'All Branches', value: ''}, ...this.orgOptions]
    },
    insurancePartnerOptionsWithAll() {
      return [{text: 'All Insurance Partners', value: ''}, ...this.insurancePartnerOptions]
    },
    visibleHeaders() {
      if (this.isSuperAdmin) return this.headers
      return this.headers.filter(header => header.value !== 'branch')
    },
    hasAppliedFilters() {
      const hasSearch = !!(this.searchText || '').trim()
      const hasPartnerFilter = !!(this.selectedInsurancePartnerId || '').trim()
      const hasBranchFilter = this.isSuperAdmin && !!(this.selectedOrg || '').trim()
      return hasSearch || hasPartnerFilter || hasBranchFilter
    },
    isPageLoading() {
      return this.localLoading || this.isLoading
    }
  },
  async created() {
    this.localLoading = true
    try {
      this.setResource('agent-privileges')
      await this.fetchOrgOptions()
      await this.fetchInsurancePartnerOptions()
      this.setTextSearch('')
      if (!this.isSuperAdmin) {
        this.selectedOrg = this.getUserOrgCode()
        this.setFilterOrgCode(this.selectedOrg)
      } else if (this.orgOptions.length === 1) {
        this.selectedOrg = this.orgOptions[0].value
        this.setFilterOrgCode(this.selectedOrg)
      } else {
        this.setFilterOrgCode('')
      }
      this.setFilterInsurancePartnerId('')
      await this.fetch()
    } finally {
      this.localLoading = false
    }
  },
  methods: {
    ...mapActions('agentPrivileges', [
      'setResource',
      'fetch',
      'setTextSearch',
      'setFilterOrgCode',
      'setFilterInsurancePartnerId',
      'fetchOrgOptions',
      'fetchInsurancePartnerOptions',
      'deleteById'
    ]),
    getUserOrgCode() {
      const user = this.user || {}
      return user.org_code || user.org_codename || user.org || ''
    },
    rowNo(index, item) {
      let rowIndex = Number(index)
      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = (this.list || []).findIndex((listItem) => {
          if (item && item.id && listItem.id) return String(listItem.id) === String(item.id)
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }
      return rowIndex + 1
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    resolveInsurancePartnerName(item) {
      if (item.insurance_partner_name) return item.insurance_partner_name
      const match = this.insurancePartnerOptions.find(partner => partner.value === item.insurance_partner_id)
      return match ? match.text : (item.insurance_partner_id || '-')
    },
    async onSearch() {
      this.localLoading = true
      try {
        this.setTextSearch(this.searchText || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onOrgFilterChange() {
      this.localLoading = true
      try {
        this.setFilterOrgCode(this.selectedOrg || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onInsurancePartnerFilterChange() {
      this.localLoading = true
      try {
        this.setFilterInsurancePartnerId(this.selectedInsurancePartnerId || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async handleFilterButtonClick() {
      if (!this.hasAppliedFilters) return

      this.localLoading = true
      try {
        this.searchText = ''
        this.selectedInsurancePartnerId = ''
        this.selectedOrg = this.isSuperAdmin ? '' : this.getUserOrgCode()
        this.setTextSearch('')
        this.setFilterInsurancePartnerId('')
        this.setFilterOrgCode(this.selectedOrg)
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    goToEdit(item) {
      this.$router.push({name: 'agent-privilege-edit', params: {id: item.id}})
    },
    async remove(item) {
      if (!window.confirm('Delete this record?')) return
      this.localLoading = true
      try {
        const ok = await this.deleteById(item.id)
        if (ok) await this.fetch()
      } finally {
        this.localLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.thai { color: #64748b; font-size: 15px; font-weight: 500; margin-left: 6px; }
.subhead { color: #64748b; font-size: 13px; }
.setup-btn { text-transform: none; font-weight: 700; border-radius: 10px; }
.table-card { border: 1px solid #e6e9f0; border-radius: 10px; box-shadow: none; overflow: hidden; }
.filter-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; }
.search-wrap { flex: 3; }
.small-select {flex: 1.5; }
.filter-btn { text-transform: none; font-weight: 600; min-width: 96px; }
.filter-btn.has-filtered { border-color: #f3b5b5 !important; color: #c62828 !important; background: #fdecec; }
.campaign-wrap { line-height: 1.25; }
.campaign-sub { color: #7284a5; font-size: 13px; }
.branch-pill { display:inline-flex; align-items:center; font-size:11.5px; font-weight:700; letter-spacing:.2px; padding:4px 9px; border-radius:6px; background:#eef1f6; color:#5b6472; }
.icon-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid #e6e9f0; background: #fff; }
.table-foot { padding: 14px 16px; border-top: 1px solid #edf2fb; color: #64748b; font-size: 12px; }
.contact-table::v-deep .v-data-table-header th { color: #7c8aa5 !important; font-size: 12px; font-weight: 700; }
.contact-table::v-deep td { font-size: 14px; }
</style>
